import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle, WarningCircle, UploadSimple, MapPin, Package } from "@phosphor-icons/react";
import { createLocation, createProp, uploadLocationImage, uploadPropImage, fileUrl } from "@/lib/api";
import { toast } from "sonner";

function mappingState(name, entities) {
  const match = entities.find((e) => e.name.toLowerCase() === name.toLowerCase());
  if (!match) return { state: "unmapped", entity: null };
  if (match.image_paths && match.image_paths.length > 0) return { state: "locked", entity: match };
  return { state: "unmapped", entity: match };
}

const BADGE_STYLES = {
  locked: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  unmapped: "text-amber-500 bg-amber-500/10 border-amber-500/20",
};
const BADGE_LABELS = { locked: "LOCKED", unmapped: "UNLOCKED (OPTIONAL)" };

function LockImageDialog({ storyId, tier, entity, onLocked }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const thumb = entity.hero_image_path || (entity.image_paths || [])[0];
  const alreadyLocked = !!thumb;
  const atCap = (entity.image_paths || []).length >= 5;
  const actionLabel = alreadyLocked ? "Add / Change Image" : "Lock Base Image";
  const createEntity = tier === "location" ? createLocation : createProp;
  const uploadImage = tier === "location" ? uploadLocationImage : uploadPropImage;

  const pickFile = (e) => {
    const file = e.target.files[0];
    if (file) setPendingFile(file);
  };

  const submit = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      // Upserts by name — safe to call even if a stub already exists from manifest detection.
      const ent = await createEntity(storyId, entity.name);
      const updated = await uploadImage(ent.id, pendingFile);
      toast.success(alreadyLocked ? `Image added for "${entity.name}"` : `Base image locked for "${entity.name}"`);
      setOpen(false);
      setPendingFile(null);
      onLocked(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save base image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        data-testid={`lockprop-btn-${entity.id}`}
        onClick={() => setOpen(true)}
        disabled={atCap}
        className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-slate-400 hover:text-amber-400 transition-colors border border-slate-700 rounded-sm px-1.5 py-0.5 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <UploadSimple size={10} /> {atCap ? "5/5 Images" : actionLabel}
      </button>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wider">
            {actionLabel} — "{entity.name}"
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Up to 5 base images per {tier} ({(entity.image_paths || []).length}/5 used) — every shot referencing
            "{entity.name}" will match whichever image is set as its Hero.
          </p>
          {alreadyLocked && !pendingFile && (
            <img src={fileUrl(thumb)} alt="current" className="w-full h-24 object-contain rounded-sm border border-slate-800 opacity-70" />
          )}
          <button
            data-testid={`lockprop-picker-${entity.id}`}
            onClick={() => fileInputRef.current.click()}
            className="w-full h-32 flex flex-col items-center justify-center gap-2 border border-dashed border-slate-700 rounded-sm text-slate-500 hover:text-amber-400 hover:border-amber-500/50 transition-colors overflow-hidden"
          >
            {pendingFile ? (
              <img src={URL.createObjectURL(pendingFile)} alt="preview" className="h-full object-contain" />
            ) : (
              <>
                <UploadSimple size={20} />
                <span className="text-xs">Click to choose an image</span>
              </>
            )}
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={pickFile} />
        </div>
        <DialogFooter>
          <Button
            data-testid={`lockprop-submit-${entity.id}`}
            onClick={submit}
            disabled={uploading || !pendingFile}
            className="bg-amber-600 hover:bg-amber-500 rounded-sm w-full"
          >
            {uploading ? "Saving…" : actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Badges({ storyId, tier, names, entities, isLocked, onChanged }) {
  const Icon = tier === "location" ? MapPin : Package;
  return names.map((name) => {
    const { state, entity } = mappingState(name, entities);
    const StateIcon = state === "locked" ? CheckCircle : WarningCircle;
    const thumb = entity ? entity.hero_image_path || (entity.image_paths || [])[0] : null;
    return (
      <span key={`${tier}-${name}`} data-testid={`locprop-mapping-${tier}-${name}`} className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-sm border ${BADGE_STYLES[state]}`}>
        {thumb ? (
          <img data-testid={`locprop-thumbnail-${tier}-${name}`} src={fileUrl(thumb)} alt={name} className="w-5 h-5 rounded-sm object-cover border border-current shrink-0" />
        ) : (
          <StateIcon size={12} />
        )}
        <Icon size={11} className="opacity-60" />
        {name}
        <span className="font-mono text-[9px] uppercase tracking-widest opacity-80">{BADGE_LABELS[state]}</span>
        {entity && !isLocked && <LockImageDialog storyId={storyId} tier={tier} entity={entity} onLocked={onChanged} />}
      </span>
    );
  });
}

export default function LocationPropMappingBar({ storyId, shots, locations, props, isLocked, onChanged }) {
  const locationNames = Array.from(new Set(shots.map((s) => s.location_ref).filter(Boolean)));
  const propNames = Array.from(new Set(shots.flatMap((s) => s.prop_refs || [])));
  if (locationNames.length === 0 && propNames.length === 0) return null;

  return (
    <div data-testid="location-prop-mapping-bar" className="border border-slate-800 rounded-sm p-3 bg-slate-900/50 space-y-2">
      <h3 className="font-heading uppercase tracking-wider text-xs font-semibold text-slate-400">
        Locations & Props Detected <span className="text-slate-600 font-normal">(optional — unlocked entities generate freely, no reference lock)</span>
      </h3>
      <div className="flex flex-wrap gap-2">
        <Badges storyId={storyId} tier="location" names={locationNames} entities={locations} isLocked={isLocked} onChanged={onChanged} />
        <Badges storyId={storyId} tier="prop" names={propNames} entities={props} isLocked={isLocked} onChanged={onChanged} />
      </div>
    </div>
  );
}
